import { _mock } from './_mock';

// ----------------------------------------------------------------------

export const _carouselsMembers = Array.from({ length: 6 }, (_, index) => ({
  id: _mock.id(index),
  name: _mock.fullName(index),
  role: _mock.role(index),
  avatarUrl: _mock.image.portrait(index),
}));

// ----------------------------------------------------------------------

export const _faqs = Array.from({ length: 8 }, (_, index) => ({
  id: _mock.id(index),
  value: `panel${index + 1}`,
  title: `Questions ${index + 1}`,
  content: _mock.description(index),
}));

// ----------------------------------------------------------------------

export const _addressBooks = Array.from({ length: 24 }, (_, index) => ({
  id: _mock.id(index),
  primary: index === 0,
  name: _mock.fullName(index),
  email: _mock.email(index + 1),
  fullAddress: _mock.fullAddress(index),
  phoneNumber: _mock.phoneNumber(index),
  company: _mock.companyNames(index + 1),
  addressType: index === 0 ? 'Home' : 'Office',
}));

// ----------------------------------------------------------------------

export const _contacts = Array.from({ length: 20 }, (_, index) => {
  const status =
    (index % 2 && 'online') || (index % 3 && 'offline') || (index % 4 && 'always') || 'busy';

  return {
    id: _mock.id(index),
    status,
    role: _mock.role(index),
    email: _mock.email(index),
    name: _mock.fullName(index),
    phoneNumber: _mock.phoneNumber(index),
    lastActivity: _mock.time(index),
    avatarUrl: _mock.image.avatar(index),
    address: _mock.fullAddress(index),
  };
});

// ----------------------------------------------------------------------

// Notification action types for actionable notifications
export type NotificationAction = {
  type: 'approve' | 'reject' | 'request_info' | 'view_details' | 'retry';
  label: string;
  variant: 'primary' | 'danger' | 'secondary' | 'default';
  endpoint?: string; // API endpoint to call when action is clicked
  requiresNote?: boolean; // Whether this action requires a note/reason input
};

export const _notifications = [
  // ========================================================================
  // ADMIN NOTIFICATIONS - APPROVAL REQUIRED (with actions)
  // ========================================================================

  // Admin: Transaction Approval - Limit Violation - High Priority
  {
    id: _mock.id(0),
    avatarUrl: _mock.image.avatar(0),
    type: 'transaction_approval_required',
    category: 'Approvals',
    priority: 'high' as const,
    targetRole: 'admin' as const,
    isUnRead: true,
    createdAt: _mock.time(0),
    title: `<p><strong>Approval Required:</strong> Transaction by <strong>Kumar Traders</strong> exceeds daily limit</p>`,
    metadata: {
      transactionId: 'txn_m6789xyz',
      referenceId: 'TXN20251031ABC123',
      dealerId: 'VEN-5678',
      dealerName: 'Kumar Traders',
      beneficiaryName: 'Ramesh Kumar',
      beneficiaryPhone: '+91 98765 43210',
      amount: 75000,
      reason:
        'Daily transaction count limit reached (2/1); Daily amount limit would be exceeded (₹125,000/₹50,000)',
      violationType: 'transaction_limit',
      limits: {
        perTransaction: 50000,
        dailyCount: 1,
        dailyAmount: 50000,
        currentDailyCount: 1,
        currentDailyAmount: 50000,
      },
    },
    actions: [
      {
        type: 'approve' as const,
        label: 'Approve',
        variant: 'primary' as const,
        endpoint: '/api/transactions/:transactionId/approve',
        requiresNote: false,
      },
      {
        type: 'reject' as const,
        label: 'Reject',
        variant: 'danger' as const,
        endpoint: '/api/transactions/:transactionId/reject',
        requiresNote: true,
      },
      {
        type: 'request_info' as const,
        label: 'Request Info',
        variant: 'secondary' as const,
        endpoint: '/api/transactions/:transactionId/request-info',
        requiresNote: true,
      },
    ],
  },

  // Admin: Transaction Approval - Insufficient Credit - High Priority
  {
    id: _mock.id(1),
    avatarUrl: _mock.image.avatar(1),
    type: 'transaction_approval_required',
    category: 'Approvals',
    priority: 'high' as const,
    targetRole: 'admin' as const,
    isUnRead: true,
    createdAt: _mock.time(1),
    title: `<p><strong>Approval Required:</strong> <strong>Sharma Store</strong> has insufficient credit for ₹85,000 transaction</p>`,
    metadata: {
      transactionId: 'txn_m6790xyz',
      referenceId: 'TXN20251031DEF456',
      dealerId: 'VEN-5679',
      dealerName: 'Sharma Store',
      beneficiaryName: 'Priya Sharma',
      beneficiaryPhone: '+91 98765 11111',
      amount: 85000,
      reason: 'Insufficient credit available (shortfall: ₹35,000)',
      violationType: 'insufficient_credit',
      creditInfo: {
        creditLimit: 100000,
        usedCredit: 50000,
        availableCredit: 50000,
        shortfall: 35000,
      },
    },
    actions: [
      {
        type: 'approve' as const,
        label: 'Approve',
        variant: 'primary' as const,
        endpoint: '/api/transactions/:transactionId/approve',
        requiresNote: false,
      },
      {
        type: 'reject' as const,
        label: 'Reject',
        variant: 'danger' as const,
        endpoint: '/api/transactions/:transactionId/reject',
        requiresNote: true,
      },
      {
        type: 'request_info' as const,
        label: 'Request Info',
        variant: 'secondary' as const,
        endpoint: '/api/transactions/:transactionId/request-info',
        requiresNote: true,
      },
    ],
  },

  // Admin: Transaction Approval - High Value Transaction - High Priority
  {
    id: _mock.id(2),
    avatarUrl: _mock.image.avatar(2),
    type: 'transaction_approval_required',
    category: 'Approvals',
    priority: 'high' as const,
    targetRole: 'admin' as const,
    isUnRead: true,
    createdAt: _mock.time(2),
    title: `<p><strong>High-Value Approval:</strong> ₹2,50,000 transaction by <strong>Patel Electronics</strong></p>`,
    metadata: {
      transactionId: 'txn_m6791xyz',
      referenceId: 'TXN20251031GHI789',
      dealerId: 'VEN-5680',
      dealerName: 'Patel Electronics',
      beneficiaryName: 'Amit Patel',
      beneficiaryPhone: '+91 98765 22222',
      amount: 250000,
      reason: 'High-value transaction exceeds automatic approval threshold (₹250,000 > ₹100,000)',
      violationType: 'high_value',
      threshold: 100000,
      dealerHistory: {
        totalTransactions: 45,
        totalAmount: 1250000,
        averageTransaction: 27777,
        successRate: 98.5,
      },
    },
    actions: [
      {
        type: 'approve' as const,
        label: 'Approve',
        variant: 'primary' as const,
        endpoint: '/api/transactions/:transactionId/approve',
        requiresNote: false,
      },
      {
        type: 'reject' as const,
        label: 'Reject',
        variant: 'danger' as const,
        endpoint: '/api/transactions/:transactionId/reject',
        requiresNote: true,
      },
      {
        type: 'request_info' as const,
        label: 'Request Info',
        variant: 'secondary' as const,
        endpoint: '/api/transactions/:transactionId/request-info',
        requiresNote: true,
      },
    ],
  },

  // Admin: Transaction Approval - Multiple Violations - High Priority
  {
    id: _mock.id(3),
    avatarUrl: _mock.image.avatar(3),
    type: 'transaction_approval_required',
    category: 'Approvals',
    priority: 'high' as const,
    targetRole: 'admin' as const,
    isUnRead: true,
    createdAt: _mock.time(3),
    title: `<p><strong>Approval Required:</strong> <strong>Gupta Traders</strong> - Multiple violations detected</p>`,
    metadata: {
      transactionId: 'txn_m6792xyz',
      referenceId: 'TXN20251031JKL012',
      dealerId: 'VEN-5681',
      dealerName: 'Gupta Traders',
      beneficiaryName: 'Suresh Gupta',
      beneficiaryPhone: '+91 98765 33333',
      amount: 175000,
      reason:
        'Weekly amount limit would be exceeded (₹275,000/₹100,000); High-value transaction (₹175,000 > ₹100,000)',
      violationType: 'multiple',
      violations: ['transaction_limit', 'high_value'],
      limits: {
        weeklyAmount: 100000,
        currentWeeklyAmount: 100000,
      },
      threshold: 100000,
    },
    actions: [
      {
        type: 'approve' as const,
        label: 'Approve',
        variant: 'primary' as const,
        endpoint: '/api/transactions/:transactionId/approve',
        requiresNote: false,
      },
      {
        type: 'reject' as const,
        label: 'Reject',
        variant: 'danger' as const,
        endpoint: '/api/transactions/:transactionId/reject',
        requiresNote: true,
      },
      {
        type: 'request_info' as const,
        label: 'Request Info',
        variant: 'secondary' as const,
        endpoint: '/api/transactions/:transactionId/request-info',
        requiresNote: true,
      },
    ],
  },

  // Admin: Credit Extension Request - Medium Priority
  {
    id: _mock.id(4),
    avatarUrl: _mock.image.avatar(4),
    type: 'credit_extension_request',
    category: 'Approvals',
    priority: 'medium' as const,
    targetRole: 'admin' as const,
    isUnRead: true,
    createdAt: _mock.time(4),
    title: `<p><strong>Credit Extension Request:</strong> <strong>Verma Store</strong> requests limit increase from ₹1,00,000 to ₹2,50,000</p>`,
    metadata: {
      requestId: 'cer_abc123xyz',
      dealerId: 'VEN-5682',
      dealerName: 'Verma Store',
      currentLimit: 100000,
      requestedLimit: 250000,
      increaseAmount: 150000,
      reason: 'Business expansion - increased customer base and transaction volume',
      dealerHistory: {
        memberSince: '2024-06-15',
        totalTransactions: 156,
        totalAmount: 3450000,
        currentUsedCredit: 75000,
        successRate: 99.2,
        averageCollectionTime: '18 hours',
      },
    },
    actions: [
      {
        type: 'approve' as const,
        label: 'Approve',
        variant: 'primary' as const,
        endpoint: '/api/credit/requests/:requestId/approve',
        requiresNote: false,
      },
      {
        type: 'reject' as const,
        label: 'Reject',
        variant: 'danger' as const,
        endpoint: '/api/credit/requests/:requestId/reject',
        requiresNote: true,
      },
      {
        type: 'request_info' as const,
        label: 'Request Info',
        variant: 'secondary' as const,
        endpoint: '/api/credit/requests/:requestId/request-info',
        requiresNote: true,
      },
    ],
  },

  // ========================================================================
  // ADMIN NOTIFICATIONS - INFORMATIONAL (no actions)
  // ========================================================================

  // Admin-only: Stuck transaction - High Priority
  {
    id: _mock.id(5),
    avatarUrl: null,
    type: 'transaction_stuck',
    category: 'Transactions',
    priority: 'high' as const,
    targetRole: 'admin' as const,
    isUnRead: true,
    createdAt: _mock.time(5),
    title: `<p><strong>Transaction stuck:</strong> TXN-45892 for ₹45,000 has been pending for 3 hours</p>`,
    metadata: {
      transactionId: 'TXN-45892',
      amount: 45000,
      dealerName: 'Rajesh Electronics',
      customerId: 'CUST-1234',
      pendingHours: 3,
    },
    actions: [
      {
        type: 'view_details' as const,
        label: 'View Details',
        variant: 'default' as const,
        endpoint: '/transactions/:transactionId',
      },
      {
        type: 'retry' as const,
        label: 'Retry',
        variant: 'primary' as const,
        endpoint: '/api/transactions/:transactionId/retry',
      },
    ],
  },
  // Admin-only: Cash collection overdue - High Priority
  {
    id: _mock.id(6),
    avatarUrl: _mock.image.avatar(5),
    type: 'cash_overdue',
    category: 'Cash Management',
    priority: 'high' as const,
    targetRole: 'admin' as const,
    isUnRead: true,
    createdAt: _mock.time(6),
    title: `<p><strong>Overdue collection:</strong> Dealer <strong>Sharma Store</strong> has ₹1,25,000 pending pickup for 28 hours</p>`,
    metadata: {
      dealerId: 'VEN-5678',
      dealerName: 'Sharma Store',
      amount: 125000,
      overdueHours: 28,
    },
  },
  // Admin-only: Cash picked up but not received - High Priority
  {
    id: _mock.id(7),
    avatarUrl: _mock.image.avatar(6),
    type: 'cash_mismatch',
    category: 'Cash Management',
    priority: 'high' as const,
    targetRole: 'admin' as const,
    isUnRead: true,
    createdAt: _mock.time(7),
    title: `<p><strong>Cash receipt pending:</strong> Distributor <strong>Amit Kumar</strong> marked ₹87,500 as picked up 6 hours ago, not yet received in admin</p>`,
    metadata: {
      distributorId: 'AGT-3456',
      distributorName: 'Amit Kumar',
      amount: 87500,
      pickupTime: '6 hours ago',
      dealerName: 'Patel Grocery',
    },
  },
  // Admin-only: Dealer KYC pending - Medium Priority
  {
    id: _mock.id(8),
    avatarUrl: _mock.image.avatar(7),
    type: 'kyc_pending',
    category: 'Compliance',
    priority: 'medium' as const,
    targetRole: 'admin' as const,
    isUnRead: true,
    createdAt: _mock.time(8),
    title: `<p><strong>KYC approval required:</strong> New dealer <strong>Gupta Electronics</strong> awaiting verification</p>`,
    metadata: {
      dealerId: 'VEN-5679',
      dealerName: 'Gupta Electronics',
      documents: ['Aadhaar', 'PAN', 'Shop License'],
    },
  },
  // Admin-only: Unusual activity - Medium Priority
  {
    id: _mock.id(9),
    avatarUrl: _mock.image.avatar(8),
    type: 'unusual_activity',
    category: 'Security',
    priority: 'medium' as const,
    targetRole: 'admin' as const,
    isUnRead: true,
    createdAt: _mock.time(9),
    title: `<p><strong>Unusual activity:</strong> Dealer <strong>Mehta Store</strong> has processed 15 transactions in last hour</p>`,
    metadata: {
      dealerId: 'VEN-5681',
      dealerName: 'Mehta Store',
      transactionCount: 15,
      timeWindow: '1 hour',
    },
  },
  // Admin-only: Daily reconciliation - Low Priority
  {
    id: _mock.id(10),
    avatarUrl: null,
    type: 'reconciliation',
    category: 'Reports',
    priority: 'low' as const,
    targetRole: 'admin' as const,
    isUnRead: false,
    createdAt: _mock.time(10),
    title: `<p><strong>Daily reconciliation:</strong> 245 transactions totaling ₹45,67,000 processed successfully</p>`,
    metadata: {
      totalTransactions: 245,
      totalAmount: 4567000,
      date: 'Today',
    },
  },

  // ========================================================================
  // DEALER NOTIFICATIONS - TRANSACTION UPDATES
  // ========================================================================

  // Dealer: Transaction Approved - Processing - High Priority
  {
    id: _mock.id(11),
    avatarUrl: null,
    type: 'transaction_approved',
    category: 'Transactions',
    priority: 'high' as const,
    targetRole: 'dealer' as const,
    isUnRead: true,
    createdAt: _mock.time(11),
    title: `<p><strong>Transaction Approved:</strong> Your ₹85,000 transfer to <strong>Priya Sharma</strong> has been approved and is processing</p>`,
    metadata: {
      transactionId: 'txn_m6790xyz',
      referenceId: 'TXN20251031DEF456',
      amount: 85000,
      beneficiaryName: 'Priya Sharma',
      beneficiaryPhone: '+91 98765 11111',
      approvedBy: 'Admin',
      approvedAt: _mock.time(11),
      status: 'PROCESSING',
      message:
        'Your transaction has been approved by admin and is now being processed. You will be notified once completed.',
    },
    actions: [
      {
        type: 'view_details' as const,
        label: 'View Details',
        variant: 'default' as const,
        endpoint: '/transactions/:transactionId',
      },
    ],
  },

  // Dealer: Transaction Rejected - High Priority
  {
    id: _mock.id(12),
    avatarUrl: null,
    type: 'transaction_rejected',
    category: 'Transactions',
    priority: 'high' as const,
    targetRole: 'dealer' as const,
    isUnRead: true,
    createdAt: _mock.time(12),
    title: `<p><strong>Transaction Rejected:</strong> Your ₹75,000 transfer to <strong>Ramesh Kumar</strong> has been rejected</p>`,
    metadata: {
      transactionId: 'txn_m6789xyz',
      referenceId: 'TXN20251031ABC123',
      amount: 75000,
      beneficiaryName: 'Ramesh Kumar',
      beneficiaryPhone: '+91 98765 43210',
      rejectedBy: 'Admin',
      rejectedAt: _mock.time(12),
      rejectionReason:
        'Exceeded daily transaction limits. Please try again tomorrow or contact admin for limit increase.',
      adminNotes:
        'Daily limit already used. Transaction appears legitimate but policy requires waiting period.',
    },
    actions: [
      {
        type: 'view_details' as const,
        label: 'View Details',
        variant: 'default' as const,
        endpoint: '/transactions/:transactionId',
      },
    ],
  },

  // Dealer: More Information Requested - High Priority
  {
    id: _mock.id(13),
    avatarUrl: null,
    type: 'info_requested',
    category: 'Transactions',
    priority: 'high' as const,
    targetRole: 'dealer' as const,
    isUnRead: true,
    createdAt: _mock.time(13),
    title: `<p><strong>Information Required:</strong> Admin needs more details about your ₹2,50,000 transaction</p>`,
    metadata: {
      transactionId: 'txn_m6791xyz',
      referenceId: 'TXN20251031GHI789',
      amount: 250000,
      beneficiaryName: 'Amit Patel',
      beneficiaryPhone: '+91 98765 22222',
      requestedBy: 'Admin',
      requestedAt: _mock.time(13),
      adminQuestion:
        'This is a high-value transaction. Please provide: 1) Purpose of transfer, 2) Relationship with beneficiary, 3) Source of funds documentation.',
    },
    actions: [
      {
        type: 'view_details' as const,
        label: 'Respond',
        variant: 'primary' as const,
        endpoint: '/transactions/:transactionId',
      },
    ],
  },

  // Dealer: Transaction successful - Low Priority
  {
    id: _mock.id(14),
    avatarUrl: null,
    type: 'transaction_success',
    category: 'Transactions',
    priority: 'low' as const,
    targetRole: 'dealer' as const,
    isUnRead: false,
    createdAt: _mock.time(14),
    title: `<p><strong>Transfer completed:</strong> ₹15,000 sent successfully to <strong>Sita Devi</strong></p>`,
    metadata: {
      transactionId: 'TXN-45894',
      amount: 15000,
      beneficiaryName: 'Sita Devi',
      upiId: 'sitadevi@paytm',
      utr: 'UTR123456789',
    },
  },

  // Dealer: Transaction failed - High Priority
  {
    id: _mock.id(15),
    avatarUrl: null,
    type: 'transaction_failed',
    category: 'Transactions',
    priority: 'high' as const,
    targetRole: 'dealer' as const,
    isUnRead: true,
    createdAt: _mock.time(15),
    title: `<p><strong>Transaction failed:</strong> ₹8,500 transfer to <strong>Ramesh Kumar</strong> failed due to invalid account</p>`,
    metadata: {
      transactionId: 'TXN-45895',
      amount: 8500,
      beneficiaryName: 'Ramesh Kumar',
      reason: 'Invalid account number',
      failureCode: 'INVALID_ACCOUNT',
    },
    actions: [
      {
        type: 'view_details' as const,
        label: 'View Details',
        variant: 'default' as const,
        endpoint: '/transactions/:transactionId',
      },
    ],
  },

  // ========================================================================
  // DEALER NOTIFICATIONS - CREDIT MANAGEMENT
  // ========================================================================

  // Dealer: Credit Extension Approved - Medium Priority
  {
    id: _mock.id(16),
    avatarUrl: null,
    type: 'credit_approved',
    category: 'Credit',
    priority: 'medium' as const,
    targetRole: 'dealer' as const,
    isUnRead: true,
    createdAt: _mock.time(16),
    title: `<p><strong>Credit Limit Increased:</strong> Your credit limit has been increased from ₹1,00,000 to ₹2,50,000</p>`,
    metadata: {
      requestId: 'cer_abc123xyz',
      oldLimit: 100000,
      newLimit: 250000,
      increaseAmount: 150000,
      approvedBy: 'Admin',
      approvedAt: _mock.time(16),
      effectiveDate: _mock.time(16),
    },
  },

  // Dealer: Credit Extension Rejected - Medium Priority
  {
    id: _mock.id(17),
    avatarUrl: null,
    type: 'credit_rejected',
    category: 'Credit',
    priority: 'medium' as const,
    targetRole: 'dealer' as const,
    isUnRead: true,
    createdAt: _mock.time(17),
    title: `<p><strong>Credit Request Declined:</strong> Your request to increase credit limit to ₹2,50,000 has been declined</p>`,
    metadata: {
      requestId: 'cer_def456xyz',
      currentLimit: 100000,
      requestedLimit: 250000,
      rejectedBy: 'Admin',
      rejectedAt: _mock.time(17),
      rejectionReason:
        'Insufficient transaction history. Please maintain current limit for 3 more months and reapply.',
      adminNotes:
        'Dealer needs to demonstrate consistent repayment and higher transaction volume before credit increase.',
    },
  },

  // Dealer: Credit Running Low - Medium Priority
  {
    id: _mock.id(18),
    avatarUrl: null,
    type: 'credit_low',
    category: 'Credit',
    priority: 'medium' as const,
    targetRole: 'dealer' as const,
    isUnRead: true,
    createdAt: _mock.time(18),
    title: `<p><strong>Credit Low:</strong> You have only ₹15,000 available credit remaining (15% of limit)</p>`,
    metadata: {
      creditLimit: 100000,
      usedCredit: 85000,
      availableCredit: 15000,
      percentageRemaining: 15,
      usedPercentage: 85,
      recommendation: 'Consider arranging cash collection or requesting a credit limit increase.',
    },
    actions: [
      {
        type: 'view_details' as const,
        label: 'Request Increase',
        variant: 'primary' as const,
        endpoint: '/credit/request-extension',
      },
    ],
  },

  // Dealer: Transaction Limit Approaching - Low Priority
  {
    id: _mock.id(19),
    avatarUrl: null,
    type: 'limit_approaching',
    category: 'Limits',
    priority: 'low' as const,
    targetRole: 'dealer' as const,
    isUnRead: false,
    createdAt: _mock.time(19),
    title: `<p><strong>Limit Warning:</strong> You have used 4 of 5 monthly transactions for beneficiary <strong>Sita Devi</strong></p>`,
    metadata: {
      beneficiaryName: 'Sita Devi',
      beneficiaryPhone: '+91 98765 44444',
      limitType: 'monthly_count',
      currentUsage: 4,
      limit: 5,
      percentageUsed: 80,
      resetDate: '2025-11-01',
    },
  },

  // Dealer: Cash collection reminder - Medium Priority
  {
    id: _mock.id(20),
    avatarUrl: null,
    type: 'cash_reminder',
    category: 'Cash Management',
    priority: 'medium' as const,
    targetRole: 'dealer' as const,
    isUnRead: false,
    createdAt: _mock.time(20),
    title: `<p><strong>Cash collection due:</strong> You have ₹52,000 pending handover to pickup distributor</p>`,
    metadata: {
      amount: 52000,
      usedCredit: 52000,
      dueHours: 18,
      distributorName: 'Pickup Distributor',
    },
  },

  // Dealer: Beneficiary verified - Low Priority
  {
    id: _mock.id(21),
    avatarUrl: null,
    type: 'beneficiary_verified',
    category: 'Beneficiaries',
    priority: 'low' as const,
    targetRole: 'dealer' as const,
    isUnRead: false,
    createdAt: _mock.time(21),
    title: `<p><strong>Beneficiary verified:</strong> Account for <strong>Priya Sharma</strong> has been verified successfully</p>`,
    metadata: {
      beneficiaryName: 'Priya Sharma',
      accountNumber: '****1234',
      bankName: 'HDFC Bank',
    },
  },
  // ========================================================================
  // DISTRIBUTOR NOTIFICATIONS - CASH PICKUPS
  // ========================================================================

  // Distributor: New pickup assigned - High Priority
  {
    id: _mock.id(22),
    avatarUrl: _mock.image.avatar(9),
    type: 'pickup_assigned',
    category: 'Pickup',
    priority: 'high' as const,
    targetRole: 'distributor' as const,
    isUnRead: true,
    createdAt: _mock.time(22),
    title: `<p><strong>New pickup:</strong> Collect ₹65,000 from <strong>Verma Store</strong>, Andheri West</p>`,
    metadata: {
      dealerId: 'VEN-5680',
      dealerName: 'Verma Store',
      amount: 65000,
      location: 'Shop 12, Andheri West, Mumbai',
      phone: '+91 98765 43210',
      pickupId: 'PKP-12345',
      assignedAt: _mock.time(22),
    },
    actions: [
      {
        type: 'view_details' as const,
        label: 'View Route',
        variant: 'primary' as const,
        endpoint: '/pickups/:pickupId',
      },
    ],
  },

  // Distributor: Urgent Pickup - Dealer Credit Exceeded - High Priority
  {
    id: _mock.id(23),
    avatarUrl: _mock.image.avatar(10),
    type: 'pickup_urgent',
    category: 'Pickup',
    priority: 'high' as const,
    targetRole: 'distributor' as const,
    isUnRead: true,
    createdAt: _mock.time(23),
    title: `<p><strong>Urgent Pickup:</strong> <strong>Kumar Traders</strong> has ₹1,25,000 used credit - immediate collection required</p>`,
    metadata: {
      dealerId: 'VEN-5683',
      dealerName: 'Kumar Traders',
      amount: 125000,
      creditLimit: 100000,
      overdueAmount: 25000,
      location: 'Shop 5, Malad East, Mumbai',
      phone: '+91 98765 55555',
      pickupId: 'PKP-12346',
      urgencyReason: 'Credit limit exceeded by ₹25,000',
    },
    actions: [
      {
        type: 'view_details' as const,
        label: 'Accept Pickup',
        variant: 'primary' as const,
        endpoint: '/pickups/:pickupId/accept',
      },
    ],
  },

  // Distributor: Pickup completed - Low Priority
  {
    id: _mock.id(24),
    avatarUrl: null,
    type: 'pickup_completed',
    category: 'Pickup',
    priority: 'low' as const,
    targetRole: 'distributor' as const,
    isUnRead: false,
    createdAt: _mock.time(24),
    title: `<p><strong>Pickup confirmed:</strong> ₹42,000 collected from <strong>Singh Traders</strong></p>`,
    metadata: {
      dealerName: 'Singh Traders',
      dealerId: 'VEN-5684',
      amount: 42000,
      pickupId: 'PKP-12344',
      completedAt: _mock.time(24),
    },
  },

  // Distributor: Multiple Pickups Available - Medium Priority
  {
    id: _mock.id(25),
    avatarUrl: null,
    type: 'pickup_batch',
    category: 'Pickup',
    priority: 'medium' as const,
    targetRole: 'distributor' as const,
    isUnRead: true,
    createdAt: _mock.time(25),
    title: `<p><strong>Batch Pickup:</strong> 5 dealers in your area have pending collections totaling ₹2,35,000</p>`,
    metadata: {
      pickupCount: 5,
      totalAmount: 235000,
      area: 'Andheri - Malad Area',
      dealers: [
        { name: 'Sharma Store', amount: 45000 },
        { name: 'Patel Traders', amount: 67000 },
        { name: 'Gupta Electronics', amount: 52000 },
        { name: 'Verma Shop', amount: 38000 },
        { name: 'Kumar Store', amount: 33000 },
      ],
    },
    actions: [
      {
        type: 'view_details' as const,
        label: 'View Route',
        variant: 'primary' as const,
        endpoint: '/pickups/batch',
      },
    ],
  },
];

// ----------------------------------------------------------------------

export const _mapContact = [
  { latlng: [33, 65], address: _mock.fullAddress(1), phoneNumber: _mock.phoneNumber(1) },
  { latlng: [-12.5, 18.5], address: _mock.fullAddress(2), phoneNumber: _mock.phoneNumber(2) },
];

// ----------------------------------------------------------------------

export const _socials = [
  {
    value: 'facebook',
    label: 'Facebook',
    path: 'https://www.facebook.com/raj.sharma',
  },
  {
    value: 'instagram',
    label: 'Instagram',
    path: 'https://www.instagram.com/raj.sharma',
  },
  {
    value: 'linkedin',
    label: 'Linkedin',
    path: 'https://www.linkedin.com/raj.sharma',
  },
  {
    value: 'twitter',
    label: 'Twitter',
    path: 'https://www.twitter.com/raj.sharma',
  },
];

// ----------------------------------------------------------------------

export const _pricingPlans = [
  {
    subscription: 'basic',
    price: 0,
    caption: 'Forever',
    lists: ['3 prototypes', '3 boards', 'Up to 5 team members'],
    labelAction: 'Current plan',
  },
  {
    subscription: 'starter',
    price: 399,
    caption: 'Saving ₹2000 a year',
    lists: [
      '3 prototypes',
      '3 boards',
      'Up to 5 team members',
      'Advanced security',
      'Issue escalation',
    ],
    labelAction: 'Choose starter',
  },
  {
    subscription: 'premium',
    price: 799,
    caption: 'Saving ₹10000 a year',
    lists: [
      '3 prototypes',
      '3 boards',
      'Up to 5 team members',
      'Advanced security',
      'Issue escalation',
      'Issue development license',
      'Permissions & workflows',
    ],
    labelAction: 'Choose premium',
  },
];

// ----------------------------------------------------------------------

export const _testimonials = [
  {
    name: _mock.fullName(1),
    postedDate: _mock.time(1),
    ratingNumber: _mock.number.rating(1),
    avatarUrl: _mock.image.avatar(1),
    content: `Excellent Work! Thanks a lot!`,
  },
  {
    name: _mock.fullName(2),
    postedDate: _mock.time(2),
    ratingNumber: _mock.number.rating(2),
    avatarUrl: _mock.image.avatar(2),
    content: `It's a very good dashboard and we are really liking the product . We've done some things, like migrate to TS and implementing a react useContext api, to fit our job methodology but the product is one of the best in terms of design and application architecture. The team did a really good job.`,
  },
  {
    name: _mock.fullName(3),
    postedDate: _mock.time(3),
    ratingNumber: _mock.number.rating(3),
    avatarUrl: _mock.image.avatar(3),
    content: `Customer support is realy fast and helpful the desgin of this theme is looks amazing also the code is very clean and readble realy good job !`,
  },
  {
    name: _mock.fullName(4),
    postedDate: _mock.time(4),
    ratingNumber: _mock.number.rating(4),
    avatarUrl: _mock.image.avatar(4),
    content: `Amazing, really good code quality and gives you a lot of examples for implementations.`,
  },
  {
    name: _mock.fullName(5),
    postedDate: _mock.time(5),
    ratingNumber: _mock.number.rating(5),
    avatarUrl: _mock.image.avatar(5),
    content: `Got a few questions after purchasing the product. The owner responded very fast and very helpfull. Overall the code is excellent and works very good. 5/5 stars!`,
  },
  {
    name: _mock.fullName(6),
    postedDate: _mock.time(6),
    ratingNumber: _mock.number.rating(6),
    avatarUrl: _mock.image.avatar(6),
    content: `CEO of Codealy.io here. We’ve built a developer assessment platform that makes sense - tasks are based on git repositories and run in virtual machines. We automate the pain points - storing candidates code, running it and sharing test results with the whole team, remotely. Bought this template as we need to provide an awesome dashboard for our early customers. I am super happy with purchase. The code is just as good as the design. Thanks!`,
  },
];
